import React, { useEffect, useMemo, useState } from 'react';
import '../styles/HomePage.css';
import { collection, onSnapshot } from 'firebase/firestore';
import { db, auth } from '../firebase/firebaseConfig';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faArrowRight,
    faBolt,
    faCalendarCheck,
    faEnvelopeOpenText,
    faFileLines,
    faMagnifyingGlass,
    faPlus,
    faScaleBalanced,
} from '@fortawesome/free-solid-svg-icons';
import bgShapes from '../assets/BG-shapes-1.png';

const HomePage = () => {
    const [eventos, setEventos] = useState([]);
    const [inboxes, setInboxes] = useState([]);
    const userEmail = auth.currentUser?.email;
    const navigate = useNavigate();

    const dateLabel = useMemo(() => new Intl.DateTimeFormat('pt-BR', {
        weekday: 'long',
        day: '2-digit',
        month: 'long',
    }).format(new Date()), []);

    const firstName = auth.currentUser?.displayName?.split(' ')[0];

    const handleNavigation = (route) => {
        navigate(route);
        sessionStorage.setItem('currentRoute', route);
    };

    const formatEventDate = (dateValue) => {
        if (!dateValue) return 'Data a definir';

        const parts = String(dateValue).split('-');
        if (parts.length === 3) {
            const [year, month, day] = parts;
            return `${day}/${month}/${year}`;
        }
        if (parts.length === 2) {
            const [month, day] = parts;
            return `${day}/${month}`;
        }
        return dateValue;
    };

    const quickActions = [
        { label: 'Nova análise', description: 'Converse com um PDF', icon: faPlus, route: '/jimmy' },
        { label: 'Novo prazo', description: 'Organize a agenda', icon: faCalendarCheck, route: '/calendario' },
        { label: 'Buscar precedentes', description: 'Consulte o TJSP', icon: faMagnifyingGlass, route: '/jurisprudencia' },
        { label: 'Abrir modelos', description: 'Use peças salvas', icon: faFileLines, route: '/modelos' },
    ];

    const upcomingEvents = eventos.slice(0, 5);
    const recentMessages = inboxes.slice(0, 5);

    useEffect(() => {
        if (!userEmail) return undefined;

        const unsubscribe = onSnapshot(collection(db, `usuarios/${userEmail}/eventos`), (snapshot) => {
            setEventos(snapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
        });

        return () => unsubscribe();
    }, [userEmail]);

    useEffect(() => {
        if (!userEmail) return undefined;

        const unsubscribe = onSnapshot(collection(db, `usuarios/${userEmail}/inbox`), (snapshot) => {
            setInboxes(snapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
        }, (error) => {
            console.error('Erro ao acompanhar o inbox:', error);
        });

        return () => unsubscribe();
    }, [userEmail]);

    return (
        <div className="homepage-shell">
            <img className="bgshapes" src={bgShapes} alt="" aria-hidden="true" />

            <main className="main-container-homepage">
                <header className="home-page-header">
                    <div>
                        <span className="home-date">{dateLabel}</span>
                        <h1>{firstName ? `Olá, ${firstName}.` : 'Bom trabalho por aí.'}</h1>
                        <p>Seus documentos, prazos e pesquisas jurídicas em um só lugar.</p>
                    </div>

                    <div className="home-ai-status">
                        <span className="home-ai-icon"><FontAwesomeIcon icon={faBolt} /></span>
                        <div>
                            <small>Modelo jurídico</small>
                            <strong>Jimmy-A1</strong>
                        </div>
                        <span className="home-live-dot" aria-label="Disponível" />
                    </div>
                </header>

                <section className="home-actions" aria-label="Ações rápidas">
                    {quickActions.map((action) => (
                        <button
                            className="home-action-button"
                            key={action.route}
                            onClick={() => handleNavigation(action.route)}
                            type="button"
                        >
                            <span className="home-action-icon"><FontAwesomeIcon icon={action.icon} /></span>
                            <span className="home-action-copy">
                                <strong>{action.label}</strong>
                                <small>{action.description}</small>
                            </span>
                            <FontAwesomeIcon className="home-action-arrow" icon={faArrowRight} />
                        </button>
                    ))}
                </section>

                <section className="home-stats-grid" aria-label="Resumo do workspace">
                    <div className="home-stat-card">
                        <FontAwesomeIcon icon={faCalendarCheck} />
                        <div><span>{eventos.length}</span><p>eventos agendados</p></div>
                    </div>
                    <div className="home-stat-card">
                        <FontAwesomeIcon icon={faEnvelopeOpenText} />
                        <div><span>{inboxes.length}</span><p>mensagens recebidas</p></div>
                    </div>
                    <div className="home-stat-card">
                        <FontAwesomeIcon icon={faScaleBalanced} />
                        <div><span>TJSP</span><p>fonte conectada ao Jurisearch</p></div>
                    </div>
                </section>

                <section className="home-content-grid">
                    <article className="home-panel home-panel-events">
                        <div className="home-panel-header">
                            <div>
                                <span className="txt-superior">Próximos eventos</span>
                                <p>Prazos e compromissos do escritório.</p>
                            </div>
                            <button className="txt-inferior" onClick={() => handleNavigation('/calendario')} type="button">
                                <span>Ver agenda</span><FontAwesomeIcon icon={faArrowRight} />
                            </button>
                        </div>

                        <div className="container-detalhes">
                            {upcomingEvents.length > 0 ? (
                                <ul className="container-li">
                                    {upcomingEvents.map((evento) => (
                                        <li className="li-home" key={evento.id}>
                                            <span>{evento.nome || 'Evento sem título'}</span>
                                            <strong>{evento.hora || '--:--'}</strong>
                                            <small>{formatEventDate(evento.data)}</small>
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <div className="home-empty-state">Sua agenda está livre.</div>
                            )}
                        </div>
                    </article>

                    <article className="home-panel home-panel-featured">
                        <div className="home-panel-header">
                            <div>
                                <span className="txt-superior">Jurisearch</span>
                                <p>Pesquisa orientada pelo conteúdo do processo.</p>
                            </div>
                            <button className="txt-inferior" onClick={() => handleNavigation('/jurisprudencia')} type="button">
                                <span>Abrir busca</span><FontAwesomeIcon icon={faArrowRight} />
                            </button>
                        </div>

                        <div className="home-jurisearch-feature">
                            <span className="jurisearch-tag">TJSP conectado</span>
                            <h2>Encontre os precedentes mais aderentes ao seu caso.</h2>
                            <p>
                                O Jurisearch extrai a tese do PDF, consulta o tribunal e combina
                                similaridade semântica com análise jurídica do Jimmy-A1.
                            </p>
                            <div className="home-pipeline">
                                <span>PDF</span><FontAwesomeIcon icon={faArrowRight} />
                                <span>TJSP</span><FontAwesomeIcon icon={faArrowRight} />
                                <span>Ranking</span>
                            </div>
                        </div>
                    </article>

                    <article className="home-panel home-panel-inbox">
                        <div className="home-panel-header">
                            <div>
                                <span className="txt-superior">Inbox</span>
                                <p>Mensagens recentes da área do cliente.</p>
                            </div>
                            <button className="txt-inferior" onClick={() => handleNavigation('/areadocliente')} type="button">
                                <span>Ver clientes</span><FontAwesomeIcon icon={faArrowRight} />
                            </button>
                        </div>

                        <div className="container-detalhes">
                            {recentMessages.length > 0 ? (
                                <ul className="container-li">
                                    {recentMessages.map((inbox) => (
                                        <li className="li-home li-message" key={inbox.id}>
                                            <span>{inbox.mensagem}</span>
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <div className="home-empty-state">Nenhuma mensagem nova.</div>
                            )}
                        </div>
                    </article>
                </section>
            </main>
        </div>
    );
};

export default HomePage;
